import { Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColumnButtonClickEvent, RowDblClickEvent, RowRemovingEvent, ToolbarItem, ToolbarPreparingEvent } from 'devextreme/ui/data_grid';
import { DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, DxiColumnModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { BizDataGridDirective } from '../../../shared/grid/biz-grid.directive';
import { BizUserColumnDirective } from '../../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../../shared/grid/biz-grid-date.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { DateTimeService } from 'src/app/shared/datetime/datetime.service';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { ReportConfiguration, ReportEntity } from '../../models/report-configuration';
import { CurrentUserService } from 'src/app/user/services';

@Component({
  selector: 'app-reports-overview-grid',
  templateUrl: './reports-overview-grid.component.html',
  styleUrls: ['./reports-overview-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, DxiColumnModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, ChangeLogLinkComponent, ChangeLogLinkEntityDirective]
})
export class ReportsOverviewGridComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly currentUserService = inject(CurrentUserService);
  readonly bizGridFiltervalues = inject(BizGridFiltervalues);
  readonly dateTimeService = inject(DateTimeService);

  @Input() reportConfigurations: ReportConfiguration[] = [];
  @Output() readonly deleteReportConfiguration = new EventEmitter<ReportConfiguration>();

  allowAdding = false;
  allowUpdating = false;
  allowDeleting = false;
  allowShowLog = false;

  ngOnInit(): void {
    this.allowAdding = this.currentUserService.hasPermission('Reports_Action_Report_Create');
    this.allowUpdating = this.currentUserService.hasPermission('Reports_Action_Report_Edit');
    this.allowDeleting = this.currentUserService.hasPermission('Reports_Action_Report_Delete');
    this.allowShowLog = this.currentUserService.hasPermission('Reports_Action_ShowLog');
  }

  get reports(): ReportEntity[] {
    return this.reportConfigurations.map(e => e.Report);
  }

  onToolbarPreparing(event: ToolbarPreparingEvent<ReportConfiguration, number>): void {
    const toolbarItems = (event.toolbarOptions.items ?? []) as ToolbarItem[];
    const item = toolbarItems.find(entry => entry?.name === 'addRowButton');
    const options = item?.options as { onClick?: () => void };
    if (options) {
      options.onClick = () => this.addNewItem();
    }
  }

  onRowDblClick(event: RowDblClickEvent<ReportConfiguration, number>): void {
    if (this.allowUpdating) {
      const item = event.data;
      this.goToItem(item.Report.Id);
    }
  }

  onRowRemoving(event: RowRemovingEvent<ReportConfiguration, number>): void {
    this.deleteReportConfiguration.emit(event.data);
  }

  private addNewItem(): void {
    this.goToItem(0);
  }

  readonly editItem = (event: ColumnButtonClickEvent<ReportConfiguration, number>): void => {
    const item = event.row.data;
    this.goToItem(item.Report.Id);
  };

  readonly userReport = (event: ColumnButtonClickEvent<ReportConfiguration, number>): boolean => {
    const item = event.row.data;
    return !item.Report.IsSystem;
  };

  private goToItem(id: number): void {
    void this.router.navigate(['report', { id }], { relativeTo: this.activatedRoute });
  }

  readonly runItem = (event: ColumnButtonClickEvent<ReportConfiguration, number>): void => {
    const item = event.row.data;
    this.goToRun(item.Report.Id);
  };

  private goToRun(id: number): void {
    void this.router.navigate(['run', { id }], { relativeTo: this.activatedRoute });
  }

  // Using arrow functions ensures that 'this' is set correctly when called from the grid.
  readonly isSchedulingEnabled = (reportCfg: ReportConfiguration): boolean => reportCfg.ScheduledItem?.Enabled ?? false;
  readonly schedulingStart = (reportCfg: ReportConfiguration): Date | null => reportCfg.ScheduledItem?.StartDate ?? null;
  readonly schedulingEnd = (reportCfg: ReportConfiguration): Date | null => reportCfg.ScheduledItem?.EndDate ?? null;
}
